import React, { useMemo } from "react"
import { TouchableOpacity, View } from "react-native"
import FontAwesome5 from "react-native-vector-icons/FontAwesome5"
import { IconImageM } from "@common/components/icons/IconsM"
import TextM from "@common/components/text/TextM"
import { lightenHexColor } from "@common/components/button/TouchableRippleM"
import { theme } from "@common/theme/theme"

/**
 * Full-width banner on the tutor live student card showing THE subject(s) to
 * work this session — the objective linked to the current live session, with
 * the prioritized school subjects as fallback. Deliberately carried by the
 * subject color (tinted background + accent bar): tutors kept working the
 * wrong subject when the matière was only visible as tiny chips.
 *
 * Same anatomy as NextAssessmentWidget below it (icon / label+value / chevron)
 * so the card column reads as one consistent stack. Tapping opens the brief.
 */
const SessionSubjectBanner = ({
    student,
    objective = null,
    schoolSubjects = [],
    onPress = () => {},
    style = {},
}) => {
    const subjects = useMemo(() => {
        const subjectIds = objective?.subject_ids?.length
            ? objective.subject_ids
            : (student?.prioritized_school_subjects?.length
                  ? student?.prioritized_school_subjects
                  : student?.school_subjects ?? []
              ).map((s) => s?.id)

        return (subjectIds ?? [])
            .map((id) => schoolSubjects?.find((s) => s?.id == id))
            .filter(Boolean)
    }, [
        objective,
        student?.prioritized_school_subjects,
        student?.school_subjects,
        schoolSubjects,
    ])

    // Single subject → the banner is carried by that subject's color. Multiple
    // subjects → stay neutral (no subject "owns" the banner); the per-subject
    // color survives on the overlapped icon pastilles.
    const isSingle = subjects.length === 1
    const primarySubject = subjects[0]
    const accentColor = isSingle
        ? primarySubject.color
        : subjects.length > 1
          ? theme.colors.primary
          : theme.colors.lightMore
    const labelColor = isSingle ? primarySubject.color : theme.colors.grey
    const valueColor = isSingle
        ? primarySubject.color
        : theme.colors.primaryDark

    return (
        <TouchableOpacity
            onPress={onPress}
            activeOpacity={0.8}
            style={{
                backgroundColor: isSingle
                    ? lightenHexColor(accentColor, 92)
                    : theme.colors.lighter,
                borderRadius: 10,
                borderWidth: 1,
                borderColor: isSingle
                    ? lightenHexColor(accentColor, 70)
                    : theme.colors.lightMore,
                borderLeftWidth: 4,
                borderLeftColor: accentColor,
                paddingVertical: 9,
                paddingHorizontal: 12,
                flexDirection: "row",
                alignItems: "center",
                ...style,
            }}
        >
            {/* Subject icon(s) in white pastilles, overlapped when several */}
            {subjects.length > 0 && (
                <View
                    style={{
                        flexDirection: "row",
                        alignItems: "center",
                        marginRight: 10,
                    }}
                >
                    {subjects.slice(0, 3).map((subject, i) => (
                        <View
                            key={subject.id}
                            style={{
                                marginLeft: i === 0 ? 0 : -10,
                                padding: 5,
                                borderRadius: 16,
                                backgroundColor: theme.colors.white,
                                borderWidth: 1,
                                borderColor: lightenHexColor(
                                    subject?.color,
                                    60
                                ),
                            }}
                        >
                            <IconImageM uri={subject?.icon} width={18} />
                        </View>
                    ))}
                </View>
            )}
            <View style={{ flex: 1 }}>
                <TextM
                    fontSize={10}
                    fontWeight="Bold"
                    color={labelColor}
                    style={{
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        opacity: 0.8,
                    }}
                >
                    {subjects.length > 1
                        ? "Matières de la séance"
                        : "Matière de la séance"}
                </TextM>
                {subjects.length > 0 ? (
                    <TextM
                        fontSize={isSingle ? 16 : 15}
                        fontWeight="ExtraBold"
                        color={valueColor}
                        numberOfLines={2}
                        style={{ marginTop: 1 }}
                    >
                        {subjects.map((s) => s?.name).join(" · ")}
                    </TextM>
                ) : (
                    <TextM
                        fontSize={13}
                        color={theme.colors.light}
                        fontStyle="italic"
                        style={{ marginTop: 1 }}
                    >
                        Aucune matière définie — ouvre le brief
                    </TextM>
                )}
            </View>
            <FontAwesome5
                name="chevron-right"
                size={12}
                color={isSingle ? primarySubject.color : theme.colors.grey}
                style={{ marginLeft: 8, opacity: 0.7 }}
            />
        </TouchableOpacity>
    )
}

export default SessionSubjectBanner
